import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { BarChart3, TrendingUp, TrendingDown, DollarSign, Target } from 'lucide-react';

// Always use tradepayflow.emergent.host for bonbot.biz custom domain
const getApiUrl = () => {
  const hostname = window.location.hostname;
  if (hostname === 'bonbot.biz' || hostname === 'www.bonbot.biz') {
    return 'https://bonbot.biz/api';
  }
  const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
  return `${backendUrl}/api`;
};

const API = getApiUrl();

const TradeStats = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchStats();
    const interval = setInterval(fetchStats, 15000); // Update every 15 seconds
    return () => clearInterval(interval);
  }, []);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/trades/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!stats && loading) {
    return (
      <Card data-testid="stats-loading">
        <CardContent className="p-6">
          <p className="text-muted-foreground">Loading statistics...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="stats-card" data-testid="stats-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <BarChart3 className="w-5 h-5" />
          Trading Statistics
        </CardTitle>
      </CardHeader>
      <CardContent>
        {stats ? (
          <div className="stats-grid" data-testid="stats-grid">
            <div className="stat-item" data-testid="total-trades-stat">
              <div className="stat-icon">
                <Target className="w-5 h-5 text-blue-500" />
              </div>
              <div className="stat-content">
                <p className="stat-label">Total Trades</p>
                <p className="stat-value" data-testid="total-trades-value">{stats.total_trades}</p>
              </div>
            </div>

            <div className="stat-item" data-testid="wins-stat">
              <div className="stat-icon">
                <TrendingUp className="w-5 h-5 text-emerald-500" />
              </div>
              <div className="stat-content">
                <p className="stat-label">Wins</p>
                <p className="stat-value" data-testid="wins-value">{stats.wins}</p>
              </div>
            </div>

            <div className="stat-item" data-testid="losses-stat">
              <div className="stat-icon">
                <TrendingDown className="w-5 h-5 text-rose-500" />
              </div>
              <div className="stat-content">
                <p className="stat-label">Losses</p>
                <p className="stat-value" data-testid="losses-value">{stats.losses}</p>
              </div>
            </div>

            <div className="stat-item" data-testid="win-rate-stat">
              <div className="stat-icon">
                <Target className="w-5 h-5 text-purple-500" />
              </div>
              <div className="stat-content">
                <p className="stat-label">Win Rate</p>
                <p className="stat-value" data-testid="win-rate-value">{stats.win_rate}%</p>
              </div>
            </div>

            <div className="stat-item" data-testid="total-payout-stat">
              <div className="stat-icon">
                <DollarSign className="w-5 h-5 text-amber-500" />
              </div>
              <div className="stat-content">
                <p className="stat-label">Total Payout</p>
                <p className="stat-value" data-testid="total-payout-value">${stats.total_payout}</p>
              </div>
            </div>
          </div>
        ) : (
          <p className="text-muted-foreground" data-testid="no-stats-text">No statistics available yet</p>
        )}
      </CardContent>
    </Card>
  );
};

export default TradeStats;
